import { CSSProperties, useEffect, useMemo, useState } from "react";
import {
    AXNUIHeaderContainer,
    AXNUIFooterContainer
} from "@iddataweb/idw-common-ui/containers";
import {
    LocaleTypes,
    PolicyDecisionResultTypes
} from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, AXNUILayout, AXNUITheme } from "@iddataweb/idw-common-ui/types";
import { transform } from "@iddataweb/idw-common-ui/utils";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { AXNUIThemingPreviewConfigurationState } from "@idw-types/common/axnui/axnui-theming-preview-configuration-state";
import { FormattedRpServiceConfigurationAttribute } from "@idw-types/data/rp-service-configuration";
import { AXNUIThemingMainContainer } from "./axnui-theming-main-container/axnui-theming-main-container";
import { AXNUIThemingPreviewConfigurationContainer } from "./axnui-theming-preview-configuration-container/axnui-theming-preview-configuration-container";
import styles from "./axnui-theming-preview-container.less";
import { AxnAttributeTypeField, AxnAttributeTypeFieldCustomization } from "@idw-types/data/reference-data/axn";

type AXNUIThemingPreviewContainerProps = {
    currentTab: AXNUIThemingTabTypes;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    axnuiTheme: AXNUITheme;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    hasDisplayableDocumentCaptureAttributes?: boolean;
    attributes: Array<FormattedRpServiceConfigurationAttribute>;
    redirectUrls?: Array<string>;
    localizeStrings: boolean;
    mergeThemingAndLocalizedFile: boolean;
    activeLanguage: LocaleTypes;
    hasCancelButtonURLEnabled: boolean;
    previewEntityType: PreviewEntityTypes;
};

export const AXNUIThemingPreviewContainer = (props: AXNUIThemingPreviewContainerProps) => {
    const {
        currentTab,
        axnuiContent,
        axnuiLayout,
        axnuiTheme,
        axnAttributeTypeFields,
        axnAttributeTypeFieldCustomizations,
        hasDisplayableDocumentCaptureAttributes,
        attributes,
        redirectUrls,
        localizeStrings,
        mergeThemingAndLocalizedFile,
        activeLanguage,
        hasCancelButtonURLEnabled,
        previewEntityType
    } = props;

    const [previewConfigurationState, setPreviewConfigurationState] = useState<AXNUIThemingPreviewConfigurationState>({
        showAsyncButton: false,
        showAfterCapturePreview: false,
        showAsyncModal: false,
        showAsyncSubmissionInProgress: false,
        showContinueInfoEmail: false,
        showNoRetriesLeft: false,
        selectedErrorCode: null,
        showErrorPageReferenceId: false,
        showMobileCompleteErrorMessage: false,
        showKbaRetryMessage: false,
        selectedLandingPage: (redirectUrls && redirectUrls.length > 0) ? redirectUrls[0] : null,
        selectedLandingPagePolicyDecisionResultType: PolicyDecisionResultTypes.APPROVE
    });

    useEffect(() => {
        if (redirectUrls) {
            if (redirectUrls.length === 0) {
                setPreviewConfigurationState({
                    ...previewConfigurationState,
                    selectedLandingPage: null
                });
            } else if (!redirectUrls.includes(previewConfigurationState.selectedLandingPage)) {
                setPreviewConfigurationState({
                    ...previewConfigurationState,
                    selectedLandingPage: redirectUrls[0]
                });
            }
        }
    }, [redirectUrls]);

    useEffect(() => {
        const cssStyleElementId = "user-supplied-custom-css";

        if (axnuiTheme.customCss.useCustomCss) {
            const existingStyle = document.getElementById(cssStyleElementId);
            if (existingStyle) {
                existingStyle.remove();
            }

            const output = transform(axnuiTheme.customCss.cssContent, `#root-html`, '', false);
            const style = document.createElement('style');
            style.setAttribute("id", cssStyleElementId);
            style.setAttribute("type", "text/css");
            style.appendChild(document.createTextNode(output));
            document.head.appendChild(style);
        } else {
            const existingStyle = document.getElementById(cssStyleElementId);
            if (existingStyle) {
                existingStyle.remove();
            }
        }
    }, [axnuiTheme.customCss.useCustomCss, axnuiTheme.customCss.cssContent]);


    const contentContainerStyle: CSSProperties = useMemo(() => ({
        fontFamily: axnuiTheme.general.font,
        color: axnuiTheme.general.mainTextColor,
    }), [axnuiTheme.general?.font, axnuiTheme.general?.mainTextColor]);

    const showPreviewConfigurationContainer = useMemo(() => {
        if (
            (
                [AXNUIThemingTabTypes.ACTION_BUTTON, AXNUIThemingTabTypes.FORM_CONTENT].includes(currentTab) &&
                hasDisplayableDocumentCaptureAttributes
            ) ||
            [
                AXNUIThemingTabTypes.LANDING_PAGE,
                AXNUIThemingTabTypes.ERROR_PAGE,
                AXNUIThemingTabTypes.MOBILE_COMPLETE_PAGE,
                AXNUIThemingTabTypes.KBA_FORM
            ].includes(currentTab)
        ) {
            return true;
        }
        return false;
    }, [currentTab, hasDisplayableDocumentCaptureAttributes]);

    return (
        <div className={styles.axnuiThemingPreviewContainer}>
            {showPreviewConfigurationContainer && (
                <AXNUIThemingPreviewConfigurationContainer
                    currentTab={currentTab}
                    redirectUrls={redirectUrls}
                    previewConfigurationState={previewConfigurationState}
                    setPreviewConfigurationState={setPreviewConfigurationState}
                />
            )}
            <div id="root-html">
                <div>
                    <div id="root">
                        <div id={"content-container"} className={styles.axnuiThemingPreviewApplicationContainer} style={contentContainerStyle}>
                            {axnuiLayout.isHeaderEnabled && (
                                <AXNUIHeaderContainer
                                    customCssHeader={axnuiContent.header.customCssHeader}
                                    customHtmlHeader={axnuiContent.header.customHtmlHeader}
                                    text={axnuiContent.header.text}
                                    logoAltText={axnuiContent.header.logoAltText}
                                    defaultFontFamily={axnuiTheme.general.font}
                                    defaultTextColor={axnuiTheme.general.mainTextColor}
                                    idpName={null}
                                    alignLogo={axnuiTheme.header.alignLogo}
                                    alignText={axnuiTheme.header.alignText}
                                    backgroundColor={axnuiTheme.header.backgroundColor}
                                    logoUrl={axnuiTheme.header.logoUrl}
                                    showLogo={axnuiTheme.header.showLogo}
                                    showText={axnuiTheme.header.showText}
                                    textColor={axnuiTheme.header.textColor}
                                    useHtmlCss={axnuiTheme.header.useHtmlCss}
                                    customHeaderHtml={null}
                                    useThemedUI={true}
                                    v2LogoPosition={null}
                                    v2LogoUrl={null}
                                />)}

                            <AXNUIThemingMainContainer
                                currentTab={currentTab}
                                axnuiContent={axnuiContent}
                                axnuiLayout={axnuiLayout}
                                axnuiTheme={axnuiTheme}
                                attributes={attributes}
                                axnAttributeTypeFields={axnAttributeTypeFields}
                                axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                                localizeStrings={localizeStrings}
                                mergeThemingAndLocalizedFile={mergeThemingAndLocalizedFile}
                                activeLanguage={activeLanguage}
                                hasCancelButtonURLEnabled={hasCancelButtonURLEnabled}
                                previewEntityType={previewEntityType}
                                previewConfigurationState={previewConfigurationState}
                                setPreviewConfigurationState={setPreviewConfigurationState}
                            />

                            {axnuiLayout.isFooterEnabled && (
                                <AXNUIFooterContainer
                                    useThemedUI={true}
                                    customFooterHtml={null}
                                    customCssFooter={axnuiContent.footer.customCssFooter}
                                    customHtmlFooter={axnuiContent.footer.customHtmlFooter}
                                    text={axnuiContent.footer.text}
                                    defaultFontFamily={axnuiTheme.general.font}
                                    defaultTextColor={axnuiTheme.general.mainTextColor}
                                    alignText={axnuiTheme.footer.alignText}
                                    backgroundColor={axnuiTheme.footer.backgroundColor}
                                    textColor={axnuiTheme.footer.textColor}
                                    useHtmlCss={axnuiTheme.footer.useHtmlCss}
                                />)}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};